import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "../widget-renderer.module.scss";
import localStyles from "./single-text-value-widget.module.scss";
import type { WidgetRendererProps } from "../widget-renderer";
import { useSelector } from "react-redux";
import { selectUnitsById } from "@src/reducers/units-slice";
import { getUnitById } from "@src/cloud-connect/units/utils/get-unit-by-id";
import { useLastWidgetValue } from "../use-last-widget-value";
import { DURATION_GAUGE_WIDGET_MS, isoToDurationMs } from "../constants";
import { getPlaceholderValue } from "../get-placeholder-value";
import { applyFieldTransforms } from "../utils/value-scaling";
import { formatWidgetDisplayValue } from "../utils/format-display-value";
import { NoDataInTimeframe } from "@src/components/atoms/no-data-in-timeframe/no-data-in-timeframe";
import Spinner from "@src/components/atoms/spinner/spinner";
import { TimezoneDate } from "@src/components/atoms/timezone-date/timezone-date";

const cssClassName = "single-text-value-widget";

/**
 * Single Text Value — label above a big number (or string), with the unit
 * suffix right after, and a timestamp underneath. Reads one scalar binding
 * (machine + node) through the existing `useLastWidgetValue` hook, so no
 * new fetcher is introduced. Strings and numbers both render raw through
 * the same code path; the hook accepts a string value when the bound node
 * has `data_type = "string"`.
 *
 * Visual rules:
 *  - label sits at ~80% of the value font (per user spec).
 *  - value font scales with the tile's smaller dimension; floor at 10px so
 *    the smallest tile (2x2 grid) still reads.
 *  - title is OFF by default — a single-text-value tile carries no header.
 */
export const SingleTextValueWidget = ({
  title,
  semantics,
  presentation,
  encoding,
  query,
  value,
  preview = false,
  showTitle = false,
  inViewport = true,
}: WidgetRendererProps) => {
  const units = useSelector(selectUnitsById);
  const unit = getUnitById(units, presentation?.unit);

  const live = useLastWidgetValue(
    preview ? undefined : query,
    isoToDurationMs(query?.window?.duration) ?? DURATION_GAUGE_WIDGET_MS,
    inViewport,
  );
  // `willFetch` is the single source of truth for "the widget will eventually
  // read live data" — true only when `preview=false` AND a query is
  // configured. `usePlaceholderValue` triggers the demo-render path for
  // everything else (picker catalog, freshly dropped pending tile on the
  // canvas with `query=null`), where no fetch will ever resolve the value;
  // without it the tile would render only "—" before the user binds a
  // machine/node. Mirrors counter-number, which uses `getPlaceholderValue`
  // unconditionally for the same reason.
  const willFetch = !preview && !!query;
  const usePlaceholderValue =
    !willFetch &&
    value === undefined &&
    (live.value === null || live.value === "");
  const resolvedValue = usePlaceholderValue
    ? getPlaceholderValue(semantics)
    : value ?? live.value;
  // Scale the bound value before it reaches the layout; strings and nulls
  // pass through untouched.
  const scaledValue = applyFieldTransforms(
    "value",
    resolvedValue,
    encoding?.transforms,
  );
  const isLoading = preview ? false : !!query && live.isLoading;
  const isEmpty =
    !preview &&
    value === undefined &&
    !!query &&
    (live.hasNoData || !!live.error);

  // ResizeObserver-driven font sizes. valueSize is derived from the smaller
  // of width/height so the widget stays readable in either landscape or
  // portrait tiles. label is 80% of value, unit is 50%. The single-text-value
  // widget never renders a title, so `showTitle` triggers no visible output.
  const [dims, setDims] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = wrapperRef.current;
    if (!node) return;
    const measure = () => {
      const rect = node.getBoundingClientRect();
      setDims({ width: rect.width, height: rect.height });
    };
    measure();
    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => measure());
      observer.observe(node);
    }
    window.addEventListener("resize", measure);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // Font sizes scale with the smaller of width/height so the widget stays
  // readable across both portrait and landscape tile sizes. The 1/6 fraction
  // keeps "big number" sizing honest (40px on a 240px tile, ~16px on a 96px
  // tile) without dominating the canvas. Label sits at 70% of value so the
  // hierarchy mirrors the inspiration (dashboard-text-panel-content).
  const baseDim = Math.min(dims.width, dims.height);
  const valueSize = Math.max(10, Math.min(48, Math.round(baseDim / 6)));
  const labelSize = Math.max(8, Math.min(22, Math.round(valueSize * 0.7)));
  const unitSize = Math.max(8, Math.min(22, Math.round(valueSize * 0.55)));

  // Empty user label → fall back to "Value" only when the tile is
  // unconfigured. A saved widget whose user opted out of a label stays
  // label-less (the spinner masks the pre-fetch moment anyway). The
  // picker preview and a freshly dropped pending widget both hit
  // `usePlaceholderValue` and therefore show the same "Value" label as
  // the picker tile — the user's mental model is one widget, one preview.
  const labelText =
    presentation?.label?.trim() || (usePlaceholderValue ? "Value" : "");
  const unitText =
    unit?.unit_text?.eng?.symbol || unit?.unit_text?.eng?.name || "";
  // Null collapse: numbers round to at most two decimals so 1.234 reads as
  // 1.23; strings pass through untouched; nothing resolves to the em-dash.
  const valueText = formatWidgetDisplayValue(scaledValue);
  // Demo timestamp for the unconfigured render path so the catalog and the
  // canvas "dropped preview" both show the full label/value/timestamp
  // stack. Stable across renders via `useMemo` so `TimezoneDate` doesn't
  // churn on every parent re-render.
  const demoTimestamp = useMemo<string | null>(
    () => (usePlaceholderValue ? new Date().toISOString() : null),
    [usePlaceholderValue],
  );
  const timestampToShow = live.lastTimestamp ?? demoTimestamp;

  return (
    <div className={styles["widget-renderer__body"]}>
      {isLoading ? (
        <div className={styles["widget-renderer__loading"]}>
          <Spinner />
        </div>
      ) : isEmpty ? (
        <NoDataInTimeframe />
      ) : (
        <div ref={wrapperRef} className={localStyles[cssClassName]}>
          {labelText && (
            <div
              className={localStyles[`${cssClassName}__label`]}
              style={{
                fontSize: labelSize,
                color: presentation?.label_color ?? undefined,
              }}
            >
              {labelText}
            </div>
          )}
          <div className={localStyles[`${cssClassName}__row`]}>
            <div
              className={localStyles[`${cssClassName}__value`]}
              style={{
                fontSize: valueSize,
                color: presentation?.color ?? undefined,
              }}
            >
              {valueText}
            </div>
            {unitText && (
              <div
                className={localStyles[`${cssClassName}__unit`]}
                style={{
                  fontSize: unitSize,
                  color: presentation?.color ?? undefined,
                }}
              >
                {unitText}
              </div>
            )}
          </div>
          {timestampToShow && (
            <div
              className={localStyles[`${cssClassName}__timestamp`]}
              style={{
                color: presentation?.label_color ?? undefined,
              }}
            >
              <TimezoneDate date={timestampToShow} showRelative />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
